import { useState } from 'react';

import type { ChannelPersonaSection } from '@/backendApis';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { SaveableField } from '@/components/ui/SaveableField';
import { SettingsSection } from '@/features/settings/settings-section';

import {
  useChannelPersonaQuery,
  useUpdateChannelPersonaMutation,
} from './msg-channels.query';

const PersonaSectionEditor = ({
  title,
  fieldKey,
  section,
  updatePersona,
}: {
  title: string;
  fieldKey: 'voice' | 'examples';
  section: ChannelPersonaSection;
  updatePersona: ReturnType<typeof useUpdateChannelPersonaMutation>;
}) => {
  const [draft, setDraft] = useState<string | null>(null);

  const value = draft ?? section.text;
  const dirty = draft !== null && draft !== section.text;
  // One mutation is shared by both editors, so a save in one blocks the other -
  // two in-flight saves would each rewrite the whole sections flag.
  const busy = updatePersona.isPending;

  return (
    <YStack className="gap-2">
      <p className="text-xs font-medium text-foreground">{title}</p>
      <SaveableField
        id={`channel-${fieldKey}`}
        value={value}
        dirty={dirty}
        busy={busy}
        saveDisabled={value.trim().length === 0}
        className="text-sm resize-y min-h-40 font-mono"
        onChange={setDraft}
        onSave={() =>
          updatePersona.mutate(
            { [fieldKey]: value },
            { onSuccess: () => setDraft(null) }
          )
        }
        onDiscard={() => setDraft(null)}
        statusContent={
          <span className="text-xs text-foreground-muted">
            {updatePersona.isError
              ? 'Save failed - try again'
              : section.is_override
                ? 'Overriding the shipped text'
                : 'Shipped text'}
          </span>
        }
      />
      {section.is_override && (
        <XStack>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() =>
              updatePersona.mutate(
                { [fieldKey]: null },
                { onSuccess: () => setDraft(null) }
              )
            }
          >
            Reset to shipped
          </Button>
        </XStack>
      )}
    </YStack>
  );
};

export const ChannelPersonaEditor = ({
  description,
}: {
  description: string;
}) => {
  const {
    data,
    isLoading,
    isLoadingError,
    isRefetchError,
    isFetching,
    refetch,
  } = useChannelPersonaQuery();
  const updatePersona = useUpdateChannelPersonaMutation();

  if (isLoadingError) {
    return (
      <SettingsSection.Group title="Persona (internal)">
        <YStack className="items-start gap-2">
          <p className="text-xs text-foreground-muted">
            Persona settings failed to load.
          </p>
          <Button
            variant="outline"
            size="sm"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            {isFetching ? 'Retrying...' : 'Retry'}
          </Button>
        </YStack>
      </SettingsSection.Group>
    );
  }
  if (isLoading || !data) {
    return (
      <SettingsSection.Group title="Persona (internal)">
        <p className="text-xs text-foreground-muted">
          Loading persona settings...
        </p>
      </SettingsSection.Group>
    );
  }

  return (
    <SettingsSection.Group title="Persona (internal)">
      <YStack className="gap-4">
        {isRefetchError && (
          <XStack className="items-center justify-between gap-3 rounded-lg border border-danger-6 bg-danger-3 px-3 py-2">
            <p className="text-xs text-danger-11">
              Couldn't refresh persona settings. Showing the last loaded values.
            </p>
            <Button
              variant="outline"
              size="xs"
              disabled={isFetching}
              onClick={() => void refetch()}
            >
              {isFetching ? 'Retrying...' : 'Retry'}
            </Button>
          </XStack>
        )}
        <p className="text-xs text-foreground-muted">{description}</p>
        <p className="text-xs font-semibold text-foreground">
          {'⚠️'} Every save (or reset) starts a fresh conversation thread: the
          agent forgets the current channel chat, and its mode goes back to
          regular.
        </p>
        <PersonaSectionEditor
          title="Voice"
          fieldKey="voice"
          section={data.voice}
          updatePersona={updatePersona}
        />
        <PersonaSectionEditor
          title="Examples"
          fieldKey="examples"
          section={data.examples}
          updatePersona={updatePersona}
        />
      </YStack>
    </SettingsSection.Group>
  );
};
